import type ClassicEditorType from "@ckeditor/ckeditor5-build-classic";
import {
  createEffect,
  createSignal,
  type JSX,
  onMount,
  Show,
  splitProps,
  useTransition,
} from "solid-js";

const ClassicEditor = () => import("@ckeditor/ckeditor5-build-classic");

type RichTextAreaFieldProps = {
  ref: (element: HTMLTextAreaElement) => void;
  name: string;
  value: string | undefined;
  onInput: JSX.EventHandler<HTMLTextAreaElement, InputEvent>;
  onChange: JSX.EventHandler<HTMLTextAreaElement, Event>;
  onBlur: JSX.EventHandler<HTMLTextAreaElement, FocusEvent>;
  placeholder?: string;
  required?: boolean;
  class?: string;
  label?: string;
  error?: string;
  rows?: number;
};

const editorConfig = {
  licenseKey: "GPL",
  toolbar: {
    items: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "|",
      "bulletedList",
      "numberedList",
    ],
    shouldNotGroupWhenFull: false,
  },
};

const RichTextAreaField = (props: RichTextAreaFieldProps) => {
  const [, inputProps] = splitProps(props, [
    "class",
    "value",
    "label",
    "error",
  ]);

  const [value, setValue] = createSignal<string | undefined>(props.value);
  const [, startTransition] = useTransition();

  let editorDiv!: HTMLDivElement;
  let textareaRef!: HTMLTextAreaElement;
  let classicEditor!: ClassicEditorType;

  createEffect((prevValue) => {
    if (props.value && prevValue !== props.value) {
      startTransition(() => {
        setValue(props.value);
      });
    }
    return props.value;
  }, props.value);

  createEffect(() => {
    if (classicEditor?.getData() !== value()) {
      classicEditor?.setData(value() ?? "");
    }
  });

  onMount(async () => {
    ClassicEditor().then((ck) => {
      ck.default
        .create(editorDiv, editorConfig)
        .then((editor) => {
          classicEditor = editor;
          editor.setData(props.value ?? "");
          editor.model.document.on("change:data", () => {
            const data = editor.getData();
            setValue(data);
          });
        })
        .catch((error) => {
          console.error(error);
        });
    });
  });

  const setRef = (ref: HTMLTextAreaElement) => {
    textareaRef = ref;
    props.ref(ref);
  };

  createEffect(() => {
    if (props.value !== value()) {
      textareaRef.dispatchEvent(
        new Event("input", { bubbles: true, cancelable: true }),
      );
    }
  });

  return (
    <div class={`w-full ${props.class}`}>
      <div>
        <span>
          {props.label}
          <Show when={props.required}>
            <b class="ms-1 text-red-500">*</b>
          </Show>
        </span>
      </div>
      <div ref={editorDiv} class="min-h-[100px]! h-full" />
      <textarea
        {...inputProps}
        required={props.required}
        value={value()}
        class="hidden w-full min-h-[100px] resize-none"
        ref={(ref) => setRef(ref)}
      />
      <div class="label">
        <span class="label-text-alt text-error">{props.error}</span>
      </div>
    </div>
  );
};

export default RichTextAreaField;
